import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../components/icons";
import { cx } from "../../components/ui";
import { formatBytes } from "../../lib/format";
import { STATUS_TONE } from "../../lib/support";
import type { SupportAttachment, SupportCategory, SupportPriority, SupportStatus } from "../../lib/types";

/**
 * The small parts every support screen repeats: the status badge, the two
 * chips, and the row an attachment renders as.
 *
 * Their own file rather than `components/ui.tsx` because they are this
 * feature's vocabulary rather than the app's — a status badge that means
 * "waiting on you" has nothing to say on a page about apps, and the shared
 * module is already the longest in the client.
 */

/** Square, hairlined, dotted — the badge shape the rest of the app uses. */
export function StatusBadge({ status, className }: { status: SupportStatus; className?: string }) {
  const { t } = useTranslation("support");
  const tone = STATUS_TONE[status];
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 border px-1.5 py-0.5 text-[11px] font-medium",
        tone.styles,
        className,
      )}
    >
      <span aria-hidden="true" className={cx("size-1.5 rounded-full", tone.dot)} />
      {t(`status.${status}.label`)}
    </span>
  );
}

/** A quiet fact: the category, the priority, the workload it is about. */
export function Chip({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1.5 border border-line bg-raised px-1.5 py-0.5 text-[11px] text-fg-muted"
    >
      {children}
    </span>
  );
}

export function CategoryChip({ category }: { category: SupportCategory }) {
  const { t } = useTranslation("support");
  return <Chip>{t(`category.${category}.label`)}</Chip>;
}

/**
 * Priority, shown only when it is not the default.
 *
 * "Normal" on every ticket is a column of noise, and the field is useful
 * precisely as an exception. Urgent gets the amber — it is the one value that
 * says something is on fire, and it should look different from the rest at a
 * glance in a list.
 */
export function PriorityChip({ priority }: { priority: SupportPriority }) {
  const { t } = useTranslation("support");
  if (priority === "normal") return null;

  const loud = priority === "urgent" || priority === "high";
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 border px-1.5 py-0.5 text-[11px]",
        loud ? "border-warn/25 bg-warn/8 text-warn" : "border-line bg-raised text-fg-muted",
      )}
    >
      {t(`priority.${priority}.label`)}
    </span>
  );
}

/**
 * One attached file, as a button that fetches it.
 *
 * A button rather than an anchor, because the API authorizes with a bearer
 * token held in memory and an `<a href>` sends no header. The caller does the
 * fetching — this only knows how to look like a file.
 */
export function AttachmentRow({
  attachment,
  onOpen,
  busy,
}: {
  attachment: SupportAttachment;
  onOpen: () => void;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={busy}
      className="group flex w-full items-center gap-2 border border-line bg-surface px-2.5 py-1.5 text-left text-[12px] text-fg-muted transition-colors duration-200 hover:border-line-strong hover:text-fg disabled:opacity-60"
    >
      <Icon name="paperclip" className="size-3.5 shrink-0 text-fg-subtle group-hover:text-accent" />
      <span className="min-w-0 flex-1 truncate">{attachment.filename}</span>
      <span className="shrink-0 tabular-nums text-fg-subtle">
        {formatBytes(attachment.sizeBytes)}
      </span>
    </button>
  );
}
